import React from 'react';

/**
 * ConfirmDialog — reusable modal confirmation prompt.
 * Replaces native window.confirm()/alert() with a styled, on-brand dialog.
 *
 * Props:
 *   title         string   — bold heading (e.g. "Delete this note?")
 *   message       string   — supporting explanation text
 *   confirmLabel  string   — text on the confirm button (default "Confirm")
 *   confirmClass  string   — Tailwind classes for the confirm button (default: blue/primary)
 *   isLoading     bool     — disables buttons and shows "Processing…" while a mutation is in flight
 *   onConfirm     fn()     — called when the user confirms
 *   onCancel      fn()     — called when the user cancels / dismisses
 */
export default function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  confirmClass = 'bg-blue-600 hover:bg-blue-700 text-white',
  isLoading = false,
  onConfirm,
  onCancel,
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95">
        {/* Icon + Title */}
        <div className="flex items-start gap-4 mb-4">
          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-5 w-5 text-red-600">
              <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            {message && <p className="text-sm text-gray-500 mt-1 leading-relaxed">{message}</p>}
          </div>
        </div>
        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 transition-colors ${confirmClass}`}>
            {isLoading ? 'Processing…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
